import { useQueryClient } from '@tanstack/react-query';
import Router from 'next/router';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Boards, type GetBoardResponse } from '@air/api';
import { type Board } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getPrivateBoardKey } from '~/components/v2/hooks/queries/usePrivateBoard';
import { CanvasQueryParams, getCanvasQueryParams } from '~/constants/canvasQueryParams';
import { createBoardRoute } from '~/constants/routes';
import { setCentralizedBoardAction, setCentralizedBoardIdAndTitleAction } from '~/store/centralizedBoard/actions';
import { type LocationBoard } from '~/store/router/types';

/**
 * PRIVATE-app board navigation (private board route + private Boards API + private board query
 * key). Board navigation was never area-fused — the public trees navigate through their own
 * contracts' `onNavigateToBoard` (PublicBoardProvider / PublicShowcaseProvider) — so this
 * is a rename for area ownership per rules/area-isolation.md, not a split.
 */
export const useGoToPrivateBoardPage = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const goToBoardPage = useCallback(
    ({
      method,
      board,
      exitCanvasFullscreen,
    }: {
      board: LocationBoard | Board;
      trackLocation?: string;
      method?: 'push' | 'replace';
      /** Drop the canvas fullscreen URL param (cnFs) so navigating exits fullscreen while keeping the canvas open. */
      exitCanvasFullscreen?: boolean;
    }): Promise<void> => {
      // `Router.ready` doesn't return a promise, so it can't be awaited directly: wrap it so
      // callers that `await goToBoardPage(...)` actually wait for navigation to finish, instead
      // of resolving as soon as `Router.ready` is called. Callers relied on the old behavior
      // (e.g. queuing an upload right after `await`ing this), which raced the router transition.
      return new Promise<void>((resolve, reject) => {
        Router.ready(async () => {
          // A throwing router call must reject, or awaiting callers never settle and stay loading.
          try {
            const boardRoute = createBoardRoute(board.id, board.title);

            // Prevents navigation if the user clicks on the same board.
            const currentBoardId = Array.isArray(Router.query.boardId) ? Router.query.boardId[0] : Router.query.boardId;

            if (currentBoardId === board.id) {
              // Same board — only drop cnFs from the URL. Do not call setFullscreen here:
              // changing fullscreen in the store triggers store→URL sync, which races this
              // shallow replace and can leave cnFs=true (fullscreen snaps back).
              if (exitCanvasFullscreen) {
                const sameBoardQuery = getCanvasQueryParams();
                delete sameBoardQuery[CanvasQueryParams.canvasFullscreen];
                await Router.replace({ pathname: boardRoute, query: sameBoardQuery }, undefined, { shallow: true });
              }

              resolve();
              return;
            }

            const cachedBoard = queryClient.getQueryData<GetBoardResponse>(getPrivateBoardKey({ boardId: board.id }));

            if (cachedBoard) {
              dispatch(setCentralizedBoardAction({ board: cachedBoard }));
            } else {
              dispatch(setCentralizedBoardIdAndTitleAction({ board }));
            }

            const query = getCanvasQueryParams();

            if (exitCanvasFullscreen) {
              delete query[CanvasQueryParams.canvasFullscreen];
            }

            const routerFn = method === 'replace' ? Router.replace : Router.push;
            await routerFn({ pathname: boardRoute, query });

            // Navigation is done; callers must not also wait on the view-tracking request.
            resolve();
          } catch (error) {
            reject(error);
            return;
          }

          try {
            await Boards.viewBoard({ boardId: board.id });
          } catch (error) {
            reportErrorToBugsnag({
              error,
              context: 'Failed to trigger a view for a board',
              metadata: { data: { boardId: board.id } },
            });
          }
        });
      });
    },
    [dispatch, queryClient],
  );

  return {
    goToBoardPage,
  };
};
